import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { MultiSelectController } from '@components-clean/core/multi-select';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { mapNamedItems } from '@pages-clean/risk-assessment/helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import {
    CriticalVariableFields_CriticalVariable_cTQEDCEntry_NamedItem as CriticalVariableFields_cTQEDCEntry,
    CriticalVariableFields_CriticalVariable_vendors_NamedItem as CriticalVariableFields_vendors,
    CriticalVariableFields_CriticalVariable_cTQType_NamedItem as CriticalVariableFields_cTQType,
} from '@queries/generated/fragments.generated';

interface AddCriticaltoQualityFactorProps {
    onClose: () => void;
    onSubmit: (data: CtQfFormInputs) => void;
    onAddFunctionalReviewsSubmit: (data: CtQfFormInputs) => void;
}

export type CtQfFormInputs = {
    isActive: boolean;
    isCritical: boolean;
    description: string;
    justification: string | null;
    type: CriticalVariableFields_cTQType | null;
    edcEntry: CriticalVariableFields_cTQEDCEntry | null;
    vendors: CriticalVariableFields_vendors[] | null;
    system: string | null;
    dataSource: string | null;
};

const COMPONENT_NAME = 'add-critical-to-quality-factor';

const AddCriticaltoQualityFactorDialog = ({
    onClose,
    onSubmit,
    onAddFunctionalReviewsSubmit,
}: AddCriticaltoQualityFactorProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.dialogs.addCtQf' });
    const { setComponentHasChanges, componentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();

    const { data: edcEntryData, loading: loadingEntryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQEDC_ENTRY },
    });
    const { data: responsibleVendorData, loading: loadingVendorData } = useQuery<getNamedItemsByType>(
        GET_NAMED_ITEMS_BY_TYPE,
        {
            variables: { type: NamedItemType.VENDOR },
        },
    );
    const { data: ctqTypeData, loading: loadingTypeData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.CTQ_TYPE },
    });

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<CtQfFormInputs>({
        mode: 'all',
        defaultValues: {
            isActive: true,
            isCritical: true,
            description: undefined,
            justification: null,
            type: null,
            edcEntry: null,
            vendors: null,
            system: null,
            dataSource: null,
        },
    });

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [isDirty]);

    const dialogTitle = `${tRoot('common:add')} ${t('title')}`;
    const primaryBtnLabel = tRoot('common:add');

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges('add-ctq-factor-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-ctq-factor-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            onAdditionalAction={() => handleSubmit(onAddFunctionalReviewsSubmit)()}
            additionalBtnLabel={t('buttons.addFunctionalReviews')}
            additionalBtnDisabled={!isValid || !isDirty || isSubmitting}
            initialHeight={731}
            initialWidth={522}
        >
            <form id="add-edit-ctq-factor" className="k-w-full k-h-full">
                <div className="k-d-flex k-flex-auto k-gap-3 k-pb-sm">
                    <Controller
                        control={control}
                        name="isActive"
                        render={({ field: { onChange, value } }) => {
                            return (
                                <CoreCheckbox
                                    data-testid="active-ctq-factor"
                                    onChange={onChange}
                                    value={value}
                                    defaultChecked
                                    label={t('fields.active')}
                                />
                            );
                        }}
                    />
                    <span>|</span>
                    <Controller
                        control={control}
                        name="isCritical"
                        render={({ field: { onChange, value } }) => {
                            return (
                                <CoreCheckbox
                                    data-testid="critical-ctq-factor"
                                    onChange={onChange}
                                    value={value}
                                    defaultChecked
                                    label={t('fields.critical')}
                                />
                            );
                        }}
                    />
                </div>
                <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={2000}
                        control={control}
                        name="description"
                        required
                        data-testid="field-select-description"
                        label={t('fields.description')}
                    />
                </div>
                <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={600}
                        control={control}
                        name="justification"
                        data-testid="field-select-justification"
                        label={t('fields.justification')}
                    />
                </div>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-sm', style.formFieldGap)}>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="type"
                            data-testid="field-select-type"
                            data={ctqTypeData?.namedItemsByType ? mapNamedItems(ctqTypeData.namedItemsByType) : []}
                            textField="name"
                            groupField="groupedBy"
                            disabled={loadingTypeData}
                            label={t('fields.type')}
                            loading={loadingTypeData}
                            dataItemKey="id"
                            filterable
                            allowClear
                        />
                    </div>
                    <div className="!k-basis-1/2">
                        <BasicDropDownController
                            control={control}
                            name="edcEntry"
                            data-testid="field-select-edcEntry"
                            data={edcEntryData?.namedItemsByType ? mapNamedItems(edcEntryData.namedItemsByType) : []}
                            textField="name"
                            groupField="groupedBy"
                            disabled={loadingEntryData}
                            label={t('fields.edcEntry')}
                            loading={loadingEntryData}
                            dataItemKey="id"
                            filterable
                            allowClear
                        />
                    </div>
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                    <MultiSelectController
                        isEditing
                        control={control}
                        name="vendors"
                        data={
                            Array.isArray(responsibleVendorData?.namedItemsByType)
                                ? mapNamedItems(responsibleVendorData.namedItemsByType)
                                : []
                        }
                        loading={loadingVendorData}
                        disabled={loadingVendorData}
                        data-testid="risk-field-vendors"
                        label={t('fields.vendors')}
                        textField="name"
                        dataItemKey="id"
                        groupField="groupedBy"
                        activeField="active"
                        filterable
                        allowCustom={false}
                    />
                </div>
                <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-sm">
                    <TextBoxController
                        control={control}
                        name="system"
                        data-testid="field-select-system"
                        label={t('fields.system')}
                        maxLength={70}
                    />
                </div>
                <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-sm">
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={500}
                        control={control}
                        name="dataSource"
                        data-testid="field-select-dataSource"
                        label={t('fields.dataSource')}
                    />
                </div>
            </form>
        </DraggableDialog>
    );
};

export default AddCriticaltoQualityFactorDialog;
